import {
  SIGN_IN_PARAMS,
  loginRequestSchema,
  centralSignInClientSchema,
  pkceChallengeSchema,
  signInReturnPathSchema,
  signInStateSchema,
  CONTROL_TOWER_SIGN_IN_CLIENT,
  type CentralSignInClient,
} from '@instanexus/contracts';

export const CENTRAL_EMAIL_PARAM = 'email';

export interface SignInRequest {
  readonly client: CentralSignInClient;
  readonly returnTo: string;
  readonly handoff: { readonly codeChallenge: string; readonly state: string } | null;
  /** A prefill hint only, never a credential. null when absent or not an email address. */
  readonly email: string | null;
}

/** Where a brand-new organization lands: platform-admin, which opens wherever a new tenant belongs. */
export const SIGN_UP_LANDING_REQUEST: SignInRequest = {
  client: 'admin',
  returnTo: '/',
  handoff: null,
  email: null,
};

/** Reads the app's sign-in request off the URL; anything malformed falls back to a safe default. */
export function parseSignInRequest(search: URLSearchParams): SignInRequest {
  const client = centralSignInClientSchema.safeParse(search.get(SIGN_IN_PARAMS.client));
  const returnTo = signInReturnPathSchema.safeParse(search.get(SIGN_IN_PARAMS.next));
  const codeChallenge = pkceChallengeSchema.safeParse(search.get(SIGN_IN_PARAMS.codeChallenge));
  const state = signInStateSchema.safeParse(search.get(SIGN_IN_PARAMS.state));
  const email = loginRequestSchema.shape.email.safeParse(search.get(CENTRAL_EMAIL_PARAM)?.trim());
  return {
    client: client.success ? client.data : 'admin',
    returnTo: returnTo.success ? returnTo.data : '/',
    handoff:
      codeChallenge.success && state.success
        ? { codeChallenge: codeChallenge.data, state: state.data }
        : null,
    email: email.success ? email.data : null,
  };
}

/** The client app's (or the partner portal's) configured origin, or null when the build has no URL for it. */
export function clientAppOrigin(client: CentralSignInClient): string | null {
  const env = import.meta.env;
  let raw: string | undefined;
  switch (client) {
    case 'admin':
      raw = env.VITE_ADMIN_URL;
      break;
    case 'visitor':
      raw = env.VITE_VISITOR_URL;
      break;
    case 'directory':
      raw = env.VITE_DIRECTORY_URL;
      break;
    case CONTROL_TOWER_SIGN_IN_CLIENT:
      raw = env.VITE_CONTROL_TOWER_URL;
      break;
    case 'partner':
      raw = env.VITE_PARTNER_URL;
      break;
    case 'express':
      // Express has no origin: it always arrives with a handoff request, and its code goes to the
      // API-built custom-scheme URL (ADR 0038 Phase 5).
      return null;
    default: {
      const unreachable: never = client;
      return unreachable;
    }
  }
  if (raw === undefined || raw.length === 0) return null;
  return raw.replace(/\/+$/, '');
}
